import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Video, Calendar, Clock, MapPin, Plus, ChevronRight, TrendingUp, BookOpen } from 'lucide-react'
import DashboardLayout from '../components/DashboardLayout'
import matchService from '../services/matchService'
import api from '../services/api'
import { useAuth } from '../context/AuthContext'
import { estVraiFree } from '../utils/planAccess'
import { T } from '../theme'
import { scoresUsThem, resultLabel } from '../utils/matchResult'

function useCountUp(target, duration = 700) {
  const [count, setCount] = useState(0)
  useEffect(() => {
    if (target === 0) { setCount(0); return }
    let v = 0; const step = target / (duration / 16)
    const t = setInterval(() => {
      v += step
      if (v >= target) { setCount(target); clearInterval(t) } else setCount(Math.floor(v))
    }, 16)
    return () => clearInterval(t)
  }, [target])
  return count
}

// Cles en MAJUSCULES : l'API renvoie le MatchStatus enum backend en
// majuscules (DRAFT, COMPLETED, ...). On normalise systematiquement
// status?.toUpperCase() avant tout lookup / comparaison.
const statusCfg = {
  DRAFT:      { label: 'Carnet',     color: T.muted,   bg: T.bgAlt,    bdr: T.rule,     icon: BookOpen },
  PENDING:    { label: 'En attente', color: T.orange,  bg: T.orangeBg, bdr: T.orangeBdr, icon: Clock },
  PROCESSING: { label: 'En cours',  color: T.blue,    bg: T.blueBg,   bdr: T.blueBdr,   icon: TrendingUp },
  COMPLETED:  { label: 'Terminé',   color: T.green,   bg: T.greenBg,  bdr: T.greenBdr,  icon: Video },
  ANALYZED:   { label: 'Analysé',   color: T.gold,    bg: T.goldBg,   bdr: T.goldBdr,   icon: Video },
  ERROR:      { label: 'Erreur',    color: T.red,     bg: T.redBg,    bdr: T.redBdr,    icon: Video },
}

function StatusBadge({ status }) {
  const cfg = statusCfg[status?.toUpperCase()] || statusCfg.DRAFT
  return (
    <span style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', padding: '3px 9px', background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.bdr}`, flexShrink: 0 }}>
      {cfg.label}
    </span>
  )
}

const STAT_ACCENTS_M = ['#0A0A0A', '#4F8EF7', '#9B6FF7', '#3DBF7A']

function KpiCard({ label, value, icon: Icon, accentIdx = 0, delay = 0 }) {
  const count = useCountUp(value, 650)
  const [vis, setVis] = useState(false)
  useEffect(() => { const t = setTimeout(() => setVis(true), delay); return () => clearTimeout(t) }, [delay])
  return (
    <div style={{
      background: T.surface, padding: '18px 20px',
      border: `0.5px solid ${T.rule}`,
      borderTop: `2.5px solid ${STAT_ACCENTS_M[accentIdx] || T.ink}`,
      opacity: vis ? 1 : 0, transform: vis ? 'translateY(0)' : 'translateY(12px)',
      transition: 'opacity .35s ease, transform .35s ease',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <span style={{ fontFamily: T.mono, fontSize: 8, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: T.muted2 }}>{label}</span>
        <Icon size={14} color={T.muted} strokeWidth={1.5} />
      </div>
      <div style={{ fontFamily: T.display, fontSize: 36, fontWeight: 800, lineHeight: 1, color: T.ink }}>{count}</div>
    </div>
  )
}

function DashboardMatches() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [matches, setMatches]   = useState([])
  const [loading, setLoading]   = useState(true)
  const [filter, setFilter]     = useState('all')
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)
  const [credits, setCredits]   = useState(null)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  useEffect(() => {
    loadMatches()
    // Crédits analyse — pour affichage informatif
    api.get('/matches/credits/status')
      .then(res => setCredits(res.data))
      .catch(() => {})
  }, [])

  const loadMatches = async () => {
    try {
      setLoading(true)
      const data = await matchService.getMatches()
      setMatches(data)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const handleNewMatch = (e) => {
    e.preventDefault()
    navigate('/dashboard/matches/new')
  }

  const formatDate = (d) => new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })

  const filters = [
    { key: 'all',        label: 'Tous' },
    { key: 'DRAFT',      label: 'Carnet' },
    { key: 'COMPLETED',  label: 'Terminés' },
    { key: 'PROCESSING', label: 'En cours' },
    { key: 'ANALYZED',   label: 'Analysés' },
  ]

  const filteredMatches = matches.filter(m => filter === 'all' || m.status?.toUpperCase() === filter)

  const stats = [
    { label: 'Total',     value: matches.length,                                                        icon: Video,      accent: true, delay: 0   },
    { label: 'Carnet',    value: matches.filter(m => m.status?.toUpperCase() === 'DRAFT').length,        icon: BookOpen,               delay: 60  },
    { label: 'En cours',  value: matches.filter(m => m.status?.toUpperCase() === 'PROCESSING').length,   icon: TrendingUp,             delay: 120 },
    { label: 'Analysés',  value: matches.filter(m => m.status?.toUpperCase() === 'ANALYZED').length,     icon: Video,                  delay: 180 },
  ]

  return (
    <DashboardLayout>
      <style>{`
        .match-link:hover { background: ${T.bgAlt} !important; }
        @keyframes fadeIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* ── HEADER ── */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'flex-start', justifyContent: 'space-between', gap: isMobile ? 16 : 0, marginBottom: 24, paddingBottom: 20, borderBottom: `0.5px solid ${T.rule}` }}>
        <div>
          <p style={{ fontFamily: T.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.15em', textTransform: 'uppercase', color: T.muted, marginBottom: 8 }}>
            Mes matchs
          </p>
          <h1 style={{ fontFamily: T.display, fontSize: isMobile ? 28 : 36, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '-.01em', color: T.ink, lineHeight: 1 }}>
            Carnet de match
          </h1>
          {/* A10 · compteur d'analyses masqué aux membres de club. */}
          {credits !== null
            && !estVraiFree(user)
            && (credits.analyses_disponibles ?? credits.analysis_credits ?? 0) > 0 && (
            <p style={{ fontFamily: T.mono, fontSize: 11, fontWeight: 500, color: T.muted, marginTop: 6 }}>
              {(() => { const n = credits.analyses_disponibles ?? credits.analysis_credits; return `${n} analyse${n !== 1 ? 's' : ''} IA disponible${n !== 1 ? 's' : ''}` })()}
            </p>
          )}
        </div>

        <a href="#" onClick={handleNewMatch} style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: isMobile ? '13px 20px' : '11px 22px',
          background: T.dark, color: '#FFFFFF',
          fontFamily: T.mono, fontSize: 11, fontWeight: 600,
          textDecoration: 'none', flexShrink: 0, alignSelf: isMobile ? 'stretch' : 'flex-start',
          justifyContent: isMobile ? 'center' : 'flex-start',
          borderRadius: T.radius, minHeight: 44,
        }}>
          <Plus size={13} /> Nouveau match
        </a>
      </div>

      {/* ── KPI ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 8, marginBottom: 20 }}>
        {stats.map((s, i) => <KpiCard key={s.label} {...s} accentIdx={i} />)}
      </div>

      {/* ── FILTRES ── */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: `0.5px solid ${T.rule}`, overflowX: 'auto' }}>
        {filters.map(({ key, label }) => (
          <button key={key} onClick={() => setFilter(key)} style={{
            flex: isMobile ? 1 : 'initial',
            padding: isMobile ? '9px 4px' : '9px 18px',
            fontSize: isMobile ? 9 : 11,
            fontFamily: T.mono, fontWeight: filter === key ? 600 : 500,
            background: 'transparent',
            color: filter === key ? T.ink : T.muted,
            borderBottom: `2px solid ${filter === key ? T.ink : 'transparent'}`,
            borderTop: 'none', borderLeft: 'none', borderRight: 'none',
            cursor: 'pointer', transition: 'all .12s', whiteSpace: 'nowrap',
          }}>
            {label}
            {!isMobile && key !== 'all' && (
              <span style={{ marginLeft: 6, fontFamily: T.mono, fontSize: 9, color: filter === key ? T.muted : T.muted2 }}>
                ({matches.filter(m => m.status?.toUpperCase() === key).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── LISTE ── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div style={{ width: 24, height: 24, border: `2px solid ${T.rule}`, borderTopColor: T.ink, borderRadius: '50%', animation: 'spin .7s linear infinite', margin: '0 auto 12px' }} />
          <p style={{ fontFamily: T.mono, fontSize: 11, fontWeight: 500, color: T.muted }}>Chargement...</p>
        </div>
      ) : filteredMatches.length === 0 ? (
        <div style={{ background: T.surface, border: `0.5px solid ${T.rule}`, borderTop: `2.5px solid ${T.ink}`, padding: '60px 24px', textAlign: 'center' }}>
          <BookOpen size={28} color={T.muted} style={{ margin: '0 auto 16px', display: 'block' }} />
          <h3 style={{ fontFamily: T.display, fontSize: 24, fontWeight: 700, textTransform: 'uppercase', marginBottom: 8, color: T.ink }}>
            {filter === 'all' ? 'Aucun match' : 'Aucun résultat'}
          </h3>
          <p style={{ fontFamily: T.mono, fontSize: 13, fontWeight: 400, color: T.muted, marginBottom: 24, lineHeight: 1.6 }}>
            {filter === 'all' ? 'Commence par saisir ton premier match.' : 'Aucun match dans cette catégorie.'}
          </p>
          {filter === 'all' && (
            <a href="#" onClick={handleNewMatch} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 24px', background: T.dark, color: '#FFFFFF', fontFamily: T.mono, fontSize: 11, fontWeight: 600, textDecoration: 'none', borderRadius: T.radius, minHeight: 44 }}>
              <Plus size={13} /> Nouveau match
            </a>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {filteredMatches.map((match, i) => {
            // Oriente club : score_home est celui de l'equipe qui recoit, donc
            // le comparer brut inversait le resultat de tout match a
            // l'exterieur (cf. src/utils/matchResult.js).
            const [scoreUs, scoreThem] = scoresUsThem(match)
            const hasScore  = scoreUs !== null
            const result    = resultLabel(match)
            const resultColor = result === 'Victoire' ? T.green : result === 'Défaite' ? T.red : T.orange
            const resultBg    = result === 'Victoire' ? T.greenBg : result === 'Défaite' ? T.redBg : T.orangeBg
            const resultBdr   = result === 'Victoire' ? T.greenBdr : result === 'Défaite' ? T.redBdr : T.orangeBdr

            return (
              <Link key={match.id} to={`/dashboard/matches/${match.id}`}
                className="match-link"
                style={{
                  display: 'flex', alignItems: 'center',
                  background: T.surface,
                  border: `0.5px solid ${T.rule}`,
                  textDecoration: 'none',
                  transition: 'background .12s',
                  animation: `fadeIn .3s ease ${i * 30}ms both`,
                  overflow: 'hidden',
                }}
              >
                <div style={{ flex: 1, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}>
                  {/* Date */}
                  <div style={{ textAlign: 'center', flexShrink: 0, width: 40 }}>
                    <div style={{ fontFamily: T.display, fontSize: 24, fontWeight: 700, lineHeight: 1, color: T.ink }}>{new Date(match.date).getDate()}</div>
                    <div style={{ fontFamily: T.mono, fontSize: 8, fontWeight: 500, textTransform: 'uppercase', color: T.muted, marginTop: 2 }}>
                      {new Date(match.date).toLocaleDateString('fr-FR', { month: 'short' })}
                    </div>
                  </div>

                  <div style={{ width: 1, height: 32, background: T.rule, flexShrink: 0 }} />

                  {/* Infos */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                      <h3 style={{ fontFamily: T.display, fontSize: isMobile ? 16 : 18, fontWeight: 700, textTransform: 'uppercase', color: T.ink, margin: 0 }}>
                        vs {match.opponent}
                      </h3>
                      <StatusBadge status={match.status} />
                      {match.category && (
                        <span style={{ fontFamily: T.mono, fontSize: 9, fontWeight: 500, padding: '2px 6px', border: `0.5px solid ${T.rule}`, color: T.muted, borderRadius: T.radius }}>
                          {match.category}
                        </span>
                      )}
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, fontFamily: T.mono, fontSize: 10, fontWeight: 400, color: T.muted }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Calendar size={10} strokeWidth={1.5} />{formatDate(match.date)}
                      </div>
                      {match.location && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <MapPin size={10} strokeWidth={1.5} />{match.location}
                        </div>
                      )}
                      {match.is_home !== undefined && (
                        <span>{match.is_home ? 'Domicile' : 'Extérieur'}</span>
                      )}
                    </div>

                    {/* Score */}
                    {hasScore && (
                      <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontFamily: T.display, fontSize: 22, fontWeight: 700, lineHeight: 1, color: T.ink }}>{scoreUs} – {scoreThem}</span>
                        {result && (
                          <span style={{ fontFamily: T.mono, fontSize: 8, fontWeight: 600, textTransform: 'uppercase', padding: '2px 8px', color: resultColor, background: resultBg, border: `1px solid ${resultBdr}`, borderRadius: 20 }}>
                            {result}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Barre progression pipeline */}
                    {match.status?.toUpperCase() === 'PROCESSING' && match.progress > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: T.mono, fontSize: 9, color: T.muted, marginBottom: 4 }}>
                          <span>Analyse en cours...</span><span>{match.progress}%</span>
                        </div>
                        <div style={{ height: 2, background: T.rule, borderRadius: 1 }}>
                          <div style={{ height: '100%', width: `${match.progress}%`, background: T.blue, borderRadius: 1, transition: 'width .3s' }} />
                        </div>
                      </div>
                    )}
                  </div>

                  <ChevronRight size={14} color={T.muted} style={{ flexShrink: 0 }} />
                </div>
              </Link>
            )
          })}
        </div>
      )}
    </DashboardLayout>
  )
}

export default DashboardMatches
